import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useFormQuestionsData } from "@/hooks/useFormRelationships";
import { QuestionGroupNode } from "@/hooks/useQuestionMappings";

interface GroupOption {
  label: string;
  path: string[];
  level: number;
  questions: string[];
}

interface HierarchicalGroupSelectorProps {
  selectedGroup: string;
  selectedQuestion: string;
  onGroupChange: (groupPath: string) => void;
  onQuestionChange: (questionId: string, questionName: string) => void;
  jotformFormId?: string | null;
  showQuestionSelector?: boolean;
  questionSelectorType?: 'radio' | 'select';
  className?: string;
}

function buildGroupOptions(
  groups: { [key: string]: QuestionGroupNode },
  parentPath: string[] = [],
  level: number = 0
): GroupOption[] {
  const options: GroupOption[] = [];

  Object.entries(groups).forEach(([groupName, node]) => {
    const currentPath = [...parentPath, groupName];
    const indent = '  '.repeat(level);
    
    // Obtener todas las preguntas de este grupo (incluyendo subgrupos)
    const allQuestions = getAllQuestionsFromNode(node);
    
    // Solo agregar opciones que tengan preguntas o subgrupos con preguntas
    if (allQuestions.length > 0) {
      options.push({
        label: `${indent}${groupName}`,
        path: currentPath,
        level,
        questions: allQuestions
      });
    }

    // Agregar subgrupos recursivamente
    if (node.subgroups && Object.keys(node.subgroups).length > 0) {
      const subOptions = buildGroupOptions(node.subgroups, currentPath, level + 1);
      options.push(...subOptions);
    }
  });

  return options;
}

function getAllQuestionsFromNode(node: QuestionGroupNode): string[] {
  // Normalizar preguntas: pueden ser strings o objetos {key, name}
  const normalizeQuestion = (q: any): string => {
    if (typeof q === 'string') return q;
    if (typeof q === 'object' && q !== null) {
      return q.name || q.key || String(q);
    }
    return String(q);
  };
  
  let questions = (node.questions || []).map(normalizeQuestion);
  
  if (node.subgroups) {
    Object.values(node.subgroups).forEach(subgroup => {
      questions.push(...getAllQuestionsFromNode(subgroup));
    });
  }
  
  return questions;
}

function getQuestionsFromPath(
  groups: { [key: string]: QuestionGroupNode },
  path: string[]
): string[] {
  // Normalizar preguntas: pueden ser strings o objetos {key, name}
  const normalizeQuestion = (q: any): string => {
    if (typeof q === 'string') return q;
    if (typeof q === 'object' && q !== null) {
      return q.name || q.key || String(q);
    }
    return String(q);
  };

  if (path.length === 0) return [];

  let current = groups[path[0]];
  if (!current) return [];

  // Si es el último nivel del path, devolver TODAS las preguntas (incluyendo subgrupos)
  if (path.length === 1) {
    return getAllQuestionsFromNode(current);
  }

  // Si hay subgrupos, continuar navegando
  if (current.subgroups) {
    return getQuestionsFromPath(current.subgroups, path.slice(1));
  }

  return [];
}

export function HierarchicalGroupSelector({
  selectedGroup,
  selectedQuestion,
  onGroupChange,
  onQuestionChange,
  jotformFormId,
  showQuestionSelector = true,
  questionSelectorType = 'select',
  className = ""
}: HierarchicalGroupSelectorProps) {
  const { formsData, isLoading } = useFormQuestionsData(jotformFormId);
  
  // Obtener los grupos del formulario seleccionado
  const selectedFormData = formsData.find(form => form.formId === jotformFormId);
  const groups = selectedFormData?.groups || {};
  
  // Construir opciones jerárquicas
  const groupOptions = buildGroupOptions(groups);
  
  // Obtener las preguntas del grupo seleccionado
  const selectedGroupPath = selectedGroup ? selectedGroup.split(' > ') : [];
  const questionsInGroup = selectedGroupPath.length > 0 
    ? getQuestionsFromPath(groups, selectedGroupPath)
    : [];
  
  // Obtener todas las preguntas del formulario para mostrar detalles
  const allQuestions = selectedFormData?.questions || [];
  
  // Debug logs
  console.log('🔍 HierarchicalGroupSelector Debug:', {
    jotformFormId,
    selectedGroup,
    selectedGroupPath,
    groups,
    groupOptions: groupOptions.length,
    questionsInGroup: questionsInGroup.length,
    allQuestions: allQuestions.length,
    selectedFormData: selectedFormData?.formTitle,
    formsData: formsData.length,
    isLoading,
    showQuestionSelector,
    shouldShowQuestionSelector: showQuestionSelector && selectedGroup && questionsInGroup.length > 0
  });
  
  const handleGroupChange = (groupPath: string) => {
    console.log('🔍 Group changed:', groupPath);
    onGroupChange(groupPath);
    // Limpiar pregunta seleccionada al cambiar grupo
    onQuestionChange("", "");
  };

  const handleQuestionChange = (questionName: string) => {
    const question = allQuestions.find(q => q.name === questionName);
    // En V2, necesitamos pasar el key (JotForm ID) en lugar del name
    const questionKey = question?.key || questionName;
    onQuestionChange(questionKey, question?.text || questionName);
  };

  if (isLoading) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-1/4 mb-2"></div>
          <div className="h-10 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Group Selector */}
      <div className="space-y-2">
        <Label>Grupo de Preguntas</Label>
        <Select value={selectedGroup} onValueChange={handleGroupChange}>
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar grupo o subgrupo" />
          </SelectTrigger>
          <SelectContent className="max-h-60 overflow-y-auto z-50 bg-background">
            {groupOptions.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground text-center">
                No hay grupos configurados para este formulario
              </div>
            ) : (
              groupOptions.map((option) => {
                const groupPath = option.path.join(' > ');
                console.log('🔍 Rendering option:', { option, groupPath });
                return (
                  <SelectItem key={groupPath} value={groupPath}>
                    <div className="flex flex-col">
                      <span className="font-medium">{option.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {option.questions.length} pregunta{option.questions.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </SelectItem>
                );
              })
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Question Selector */}
      {showQuestionSelector && selectedGroup && questionsInGroup.length > 0 && (
        <div className="space-y-2">
          <Label>Pregunta</Label>
          {questionSelectorType === 'radio' ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Pregunta del Grupo</CardTitle>
                <CardDescription className="text-xs">
                  Selecciona UNA pregunta para incluir en el cálculo
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <RadioGroup value={selectedQuestion} onValueChange={handleQuestionChange}>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {questionsInGroup.map((questionItem: any) => {
                      // Normalizar: asegurar que siempre es string
                      const questionName = typeof questionItem === 'string' 
                        ? questionItem 
                        : (questionItem?.name || questionItem?.key || String(questionItem));
                      const question = allQuestions.find(q => q.name === questionName);
                      const questionText = question?.text ? String(question.text) : questionName;
                      
                      return (
                        <div key={questionName} className="flex items-center space-x-2">
                          <RadioGroupItem value={questionName} id={questionName} />
                          <Label
                            htmlFor={questionName}
                            className="text-sm font-normal cursor-pointer flex-1"
                          >
                            {questionText}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>
          ) : (
            <Select value={selectedQuestion} onValueChange={handleQuestionChange}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar pregunta" />
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto z-50 bg-background">
                {questionsInGroup.map((questionItem: any) => {
                  // Normalizar: asegurar que siempre es string
                  const questionName = typeof questionItem === 'string' 
                    ? questionItem 
                    : (questionItem?.name || questionItem?.key || String(questionItem));
                  const question = allQuestions.find(q => q.name === questionName);
                  const questionText = question?.text ? String(question.text) : questionName;
                  
                  return (
                    <SelectItem key={questionName} value={questionName}>
                      <div className="flex flex-col">
                        <span className="font-medium">{questionName}</span>
                        <span className="text-xs text-muted-foreground">
                          {questionText}
                        </span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </>
  );
}
